import React from 'react';
import { Button } from '@components/atoms/Button';
import { Check, X, Edit } from 'lucide-react';
import { cn } from '@utils/cn';

interface ActionButtonsProps {
  showApprovalActions?: boolean;
  showEditAction?: boolean;
  loading?: boolean;
  onApprove?: () => void;
  onReject?: () => void;
  onEdit?: () => void;
  className?: string;
  /**
   * Story-066: `ActionButtons` is shared/unparameterized (rendered identically in
   * `ApprovalItem` and the Schedule table's `TaskRow`) and has no entity id of its own, so it
   * cannot self-generate a unique testid. Optional and additive — omitted call sites (e.g. the
   * Schedule table) render exactly as before, with no `data-testid` attribute at all.
   */
  approveTestId?: string;
  rejectTestId?: string;
}

export const ActionButtons: React.FC<ActionButtonsProps> = ({
  showApprovalActions = false,
  showEditAction = true,
  loading = false,
  onApprove,
  onReject,
  onEdit,
  className = '',
  approveTestId,
  rejectTestId,
}) => {
  return (
    <div
      className={cn('flex items-center justify-center space-x-2', className)}>
      {showApprovalActions && (
        <>
          <Button
            variant="tertiary"
            loading={loading}
            onClick={onApprove}
            disabled={loading}
            leftIcon={<Check className="w-4 h-4" />}
            data-testid={approveTestId}
            className="text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200">
            Approve
          </Button>
          <Button
            variant="tertiary"
            loading={loading}
            onClick={onReject}
            disabled={loading}
            leftIcon={<X className="w-4 h-4" />}
            data-testid={rejectTestId}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200">
            Reject
          </Button>
        </>
      )}

      {showEditAction && (
        <Button
          variant="primary"
          onlyIcon
          onClick={(e) => {
            e.stopPropagation();
            onEdit?.();
          }}>
          <Edit className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
};
